/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getAvatar } from "Models/User";
import cn from "classnames";
import Avatar from "components/Avatar";
import { Button, Heading, Stack, Text } from "components/index";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ICompanion } from "types/Companion";
import capitalize from "utils/capitalize";
import { pushToDataLayer } from "utils/gtm";

type IState = "completed" | "approved" | "denied" | "initial" | "pending" | "processing";

const stateStyle = {
  completed: [
    "-text--theme-colors-success-default",
    "-border--theme-colors-success-default",
    "-bg--component-colors-gd-guest-card-bg",
  ],
  approved: [
    "-text--theme-colors-success-default",
    "-border--theme-colors-success-default",
    "-bg--component-colors-gd-guest-card-bg",
  ],
  pending: [
    "-text--theme-colors-warning-default",
    "-border--theme-colors-warning-default",
    "-bg--component-colors-gd-guest-card-bg",
  ],
  initial: [
    "-text--theme-colors-warning-default",
    "-border--theme-colors-border-neutral-08",
    "-bg--component-colors-gd-guest-card-bg",
  ],
  denied: [
    "-text--theme-colors-danger-default",
    "-border--theme-colors-danger-default",
    "-bg--component-colors-gd-guest-card-bg",
  ],
  processing: [
    "-text--theme-colors-info-default",
    "-border--theme-colors-info-default",
    "-bg--component-colors-gd-guest-card-bg",
  ],
};

export const findGuestDocumentState = (
  approveMsg: string,
  denyMsg: string,
  img: string,
) => {
  if (approveMsg && approveMsg.length > 0 && approveMsg !== "denied") {
    return "completed";
  }
  if ((denyMsg && denyMsg.length > 0) || approveMsg === "denied") {
    return "denied";
  }
  if (!img || img.length === 0) {
    return "pending";
  }
  if (img.length > 0) {
    return "pending";
  }
  return "pending";
};

type Props = {
  state?: IState;
  variant?: "onboard" | "dashboard";
  companion?: ICompanion;
  handleSelectCompanion: Function;
  onViewQRCode?: () => void;
};

const CompanionCard: FC<Props> = ({
  state = "pending",
  variant = "onboard",
  companion,
  handleSelectCompanion,
  onViewQRCode,
}) => {
  const { t } = useTranslation();
  const onboardStateMessages = {
    completed: {
      note: t("companionCard.onboard.completed"),
    },
    pending: {
      note: t("companionCard.onboard.pending"),
    },
    processing: {
      note: t("companionCard.onboard.processing"),
    },
  };

  const style = stateStyle[state] || stateStyle.pending;
  if (variant === "dashboard") {
    const image = getAvatar(companion);
    return (
      <div
        className={cn(
          "w-[255px] h-full flex flex-col px-6 py-8 gap-y-5 relative border -rounded-user-card",
          "-border--theme-colors-border-neutral-08",
          "-bg--component-colors-gd-guest-card-bg",
        )}
      >
        <div className="w-full">
          <Avatar
            src={image}
            alt={companion.role === "companion" ? "companion" : "user"}
            className={cn("w-full object-cover aspect-square -rounded-image", {
              "!object-cover": !companion?.personal_image,
            })}
          />
        </div>
        <Heading
          as="h4"
          className="w-full truncate -text--component-colors-gd-guest-card-high"
        >
          {capitalize(companion?.first_name)} {companion?.last_name}
        </Heading>

        <div className="mt-auto flex items-stretch gap-x-2.5">
          <Button
            size="xl"
            variant="primary"
            className="flex-1"
            onClick={() => {
              handleSelectCompanion();
              pushToDataLayer({
                event: "view_details",
                screen_name: "guest selection",
                journey_step: "guest selection",
              });
            }}
          >
            <div className="flex items-center justify-center gap-x-s10">
              <Text as="span">{t("common.buttons.viewDetails")}</Text>
            </div>
          </Button>

          {onViewQRCode && (
            <Button
              variant="secondary"
              aria-label={t("guestDetails.formFields.userIdQRCodeTitle")}
              className="!min-h-min !p-[5px] !-rounded-icon-button shrink-0"
              onClick={() => onViewQRCode()}
            >
              <div className="p-1.5">
                <FontAwesomeIcon name="fa-qrcode" size="lg" />
              </div>
            </Button>
          )}
        </div>
      </div>
    );
  }
  if (variant === "onboard") {
    const image = getAvatar(companion);
    return (
      <div
        className={cn(
          "w-[255px] h-full flex flex-col px-6 py-8 gap-y-5 relative border  -rounded-user-card",
          ...style,
        )}
      >
        <div className="w-full">
          <Avatar
            src={image}
            alt={companion.role === "companion" ? "companion" : "user"}
            className={cn("w-full object-cover aspect-square  -rounded-image", {
              "!object-contain": !companion?.personal_image,
            })}
          />
        </div>
        <Heading
          as="h4"
          className="w-full truncate -text--component-colors-gd-guest-card-high"
        >
          {capitalize(companion?.first_name)} {companion?.last_name}
        </Heading>

        <Stack>
          {state !== "completed" ? (
            <div>
              <Button
                size="xl"
                variant="primary"
                fullWidth
                onClick={handleSelectCompanion}
              >
                <div className="flex items-center justify-center gap-x-s10">
                  {t("auth.login.signUp")}
                  <FontAwesomeIcon name="fa-chevron-right" />
                </div>
              </Button>
            </div>
          ) : (
            <div>
              <Button size="xl" fullWidth disabled variant="primary">
                {t("onboarding.complete")}
              </Button>
            </div>
          )}
        </Stack>
      </div>
    );
  }
};

export default CompanionCard;
